<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="min-width: 700px; max-width: 800px">
      <!-- Header -->
      <q-card-section class="row items-center bg-primary text-white">
        <q-icon name="settings_input_component" size="sm" class="q-mr-sm" />
        <div class="text-h6">Assign Equipment to Controller</div>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense />
      </q-card-section>

      <q-separator />

      <q-card-section v-if="equipment">
        <div class="text-subtitle1 q-mb-sm">
          Equipment: <strong>{{ equipment.name }}</strong>
        </div>
        <div class="text-caption text-grey-7 q-mb-md">
          {{ equipment.brick_class }}
        </div>

        <!-- Controller Selector -->
        <q-select
          v-model="selectedController"
          :options="filteredControllers"
          option-value="id"
          option-label="name"
          emit-value
          map-options
          label="Select Existing Controller *"
          outlined
          dense
          clearable
          :loading="loadingControllers"
          @update:model-value="handleControllerChange"
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label caption>
                  {{ scope.opt.brick_class }}
                </q-item-label>
                <q-item-label caption class="text-grey-6">
                  {{ formatIoCapacity(scope.opt.io_capacity) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>

          <template #prepend>
            <q-icon name="settings_remote" />
          </template>
        </q-select>

        <!-- Search/Filter -->
        <q-input
          v-model="searchText"
          dense
          outlined
          placeholder="Search controllers..."
          class="q-mt-md"
        >
          <template #prepend>
            <q-icon name="search" />
          </template>
        </q-input>

        <!-- Validation Result -->
        <div v-if="validation && selectedController" class="q-mt-md">
          <q-banner
            :class="validation.valid ? 'bg-positive text-white' : 'bg-negative text-white'"
            dense
            rounded
          >
            <template #avatar>
              <q-icon :name="validation.valid ? 'check_circle' : 'error'" color="white" />
            </template>
            <div class="text-subtitle2">
              {{ validation.valid ? 'Controller has sufficient capacity' : 'Insufficient I/O capacity' }}
            </div>
          </q-banner>

          <!-- Before/After Preview -->
          <div class="row q-col-gutter-md q-mt-md">
            <!-- Equipment Requirements (Before) -->
            <div class="col-6">
              <q-card flat bordered>
                <q-card-section class="bg-grey-2">
                  <div class="text-subtitle2">Equipment Requires</div>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <div class="row q-col-gutter-sm">
                    <div class="col-6 text-center">
                      <div :class="`text-h5 text-${validation.required.AI > validation.capacity.AI ? 'negative' : 'blue'}`">
                        {{ validation.required.AI || 0 }}
                      </div>
                      <div class="text-caption">AI Needed</div>
                    </div>
                    <div class="col-6 text-center">
                      <div :class="`text-h5 text-${validation.required.AO > validation.capacity.AO ? 'negative' : 'green'}`">
                        {{ validation.required.AO || 0 }}
                      </div>
                      <div class="text-caption">AO Needed</div>
                    </div>
                    <div class="col-6 text-center">
                      <div :class="`text-h5 text-${validation.required.DI > validation.capacity.DI ? 'negative' : 'orange'}`">
                        {{ validation.required.DI || 0 }}
                      </div>
                      <div class="text-caption">DI Needed</div>
                    </div>
                    <div class="col-6 text-center">
                      <div :class="`text-h5 text-${validation.required.DO > validation.capacity.DO ? 'negative' : 'red'}`">
                        {{ validation.required.DO || 0 }}
                      </div>
                      <div class="text-caption">DO Needed</div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- Controller Capacity (After) -->
            <div class="col-6">
              <q-card flat bordered>
                <q-card-section class="bg-grey-2">
                  <div class="text-subtitle2">Controller Provides</div>
                </q-card-section>
                <q-separator />
                <q-card-section>
                  <div class="row q-col-gutter-sm">
                    <div v-for="ioType in getAvailableIoTypes(validation.capacity)" :key="ioType" class="col-6 text-center">
                      <div :class="`text-h5 text-${getIoTypeColor(ioType)}`">{{ validation.capacity[ioType] || 0 }}</div>
                      <div class="text-caption">{{ ioType }} Total</div>
                      <div class="text-caption text-grey-6">({{ validation.available[ioType] || 0 }} available)</div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>

          <!-- Error Messages -->
          <q-list v-if="!validation.valid && validation.errors.length > 0" dense class="q-mt-md">
            <q-item v-for="(error, index) in validation.errors" :key="index" class="bg-red-1">
              <q-item-section avatar>
                <q-icon name="warning" color="negative" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-negative">{{ error }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>

          <!-- I/O Breakdown Details -->
          <div v-if="validation.breakdown" class="q-mt-md">
            <q-expansion-item
              icon="info"
              label="View I/O Point Details"
              caption="See which specific points are using I/O capacity"
              dense
              dense-toggle
              class="bg-grey-1 rounded-borders"
            >
              <q-card flat bordered class="q-mt-sm">
                <q-card-section>
                  <div class="row q-col-gutter-md">
                    <!-- AI Points -->
                    <div class="col-6">
                      <div class="text-subtitle2 text-blue q-mb-sm">
                        <q-icon name="input" class="q-mr-xs" />
                        Analog Inputs ({{ validation.breakdown.AI?.length || 0 }})
                      </div>
                      <q-list v-if="validation.breakdown.AI?.length > 0" dense bordered separator class="rounded-borders">
                        <q-item v-for="point in validation.breakdown.AI" :key="point.id">
                          <q-item-section>
                            <q-item-label>{{ point.name }}</q-item-label>
                            <q-item-label caption class="text-grey-7">{{ point.brick_class }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </q-list>
                      <div v-else class="text-caption text-grey-6 q-pa-sm">No AI points</div>
                    </div>

                    <!-- AO Points -->
                    <div class="col-6">
                      <div class="text-subtitle2 text-green q-mb-sm">
                        <q-icon name="output" class="q-mr-xs" />
                        Analog Outputs ({{ validation.breakdown.AO?.length || 0 }})
                      </div>
                      <q-list v-if="validation.breakdown.AO?.length > 0" dense bordered separator class="rounded-borders">
                        <q-item v-for="point in validation.breakdown.AO" :key="point.id">
                          <q-item-section>
                            <q-item-label>{{ point.name }}</q-item-label>
                            <q-item-label caption class="text-grey-7">{{ point.brick_class }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </q-list>
                      <div v-else class="text-caption text-grey-6 q-pa-sm">No AO points</div>
                    </div>

                    <!-- DI Points -->
                    <div class="col-6 q-mt-md">
                      <div class="text-subtitle2 text-orange q-mb-sm">
                        <q-icon name="sensors" class="q-mr-xs" />
                        Digital Inputs ({{ validation.breakdown.DI?.length || 0 }})
                      </div>
                      <q-list v-if="validation.breakdown.DI?.length > 0" dense bordered separator class="rounded-borders">
                        <q-item v-for="point in validation.breakdown.DI" :key="point.id">
                          <q-item-section>
                            <q-item-label>{{ point.name }}</q-item-label>
                            <q-item-label caption class="text-grey-7">{{ point.brick_class }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </q-list>
                      <div v-else class="text-caption text-grey-6 q-pa-sm">No DI points</div>
                    </div>

                    <!-- DO Points -->
                    <div class="col-6 q-mt-md">
                      <div class="text-subtitle2 text-red q-mb-sm">
                        <q-icon name="power_settings_new" class="q-mr-xs" />
                        Digital Outputs ({{ validation.breakdown.DO?.length || 0 }})
                      </div>
                      <q-list v-if="validation.breakdown.DO?.length > 0" dense bordered separator class="rounded-borders">
                        <q-item v-for="point in validation.breakdown.DO" :key="point.id">
                          <q-item-section>
                            <q-item-label>{{ point.name }}</q-item-label>
                            <q-item-label caption class="text-grey-7">{{ point.brick_class }}</q-item-label>
                          </q-item-section>
                        </q-item>
                      </q-list>
                      <div v-else class="text-caption text-grey-6 q-pa-sm">No DO points</div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </div>
        </div>

        <!-- Loading state for validation -->
        <div v-if="validating" class="q-mt-md text-center">
          <q-spinner color="primary" size="40px" />
          <div class="text-caption text-grey-7 q-mt-sm">Validating I/O capacity...</div>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right">
        <q-btn v-close-popup flat label="Cancel" :disable="assigning" />
        <q-btn
          color="primary"
          label="Assign to Controller"
          :loading="assigning"
          :disable="!canAssign"
          @click="handleAssign"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { Notify } from 'quasar';
  import { api } from '@/services/api';

  const props = defineProps({
    modelValue: { type: Boolean, default: false },
    equipment: { type: Object, default: null },
    siteId: { type: [Number, String], required: true },
  });

  const emit = defineEmits(['update:modelValue', 'assigned']);

  // State
  const controllers = ref([]);
  const loadingControllers = ref(false);
  const selectedController = ref(null);
  const searchText = ref('');
  const validation = ref(null);
  const validating = ref(false);
  const assigning = ref(false);

  // Computed
  const filteredControllers = computed(() => {
    if (!searchText.value) {return controllers.value;}

    const search = searchText.value.toLowerCase();
    return controllers.value.filter((c) =>
      c.name?.toLowerCase().includes(search) ||
      c.brick_class?.toLowerCase().includes(search),
    );
  });

  const canAssign = computed(() => {
    return selectedController.value && validation.value?.valid && !assigning.value && !validating.value;
  });

  // Methods
  async function loadControllers() {
    loadingControllers.value = true;
    try {
      // Fetch all equipment for the site and filter to controllers
      const data = await api.get(`v2/sites/${props.siteId}/equipment`);
      const allEquipment = Array.isArray(data) ? data : (data.data || []);

      // Filter to only controllers (equipment with controller_template_id or brick_class containing 'Controller')
      controllers.value = allEquipment.filter((equip) => {
        const hasControllerTemplate = !!(equip.controller_template_id);
        const isControllerClass = (equip.brick_class || '').includes('Controller');
        return hasControllerTemplate || isControllerClass;
      });
    } catch (error) {
      console.error('Failed to load controllers:', error);
      Notify.create({ type: 'negative', message: 'Failed to load controllers.' });
    } finally {
      loadingControllers.value = false;
    }
  }

  async function handleControllerChange(controllerId) {
    if (!controllerId || !props.equipment) {
      validation.value = null;
      return;
    }

    validating.value = true;
    try {
      // Validate that the selected controller has enough I/O capacity
      const data = await api.get(
        `v2/site-equipment/${props.equipment.id}/validate-controller-assignment`,
        { controller_id: controllerId },
      );
      validation.value = data;
    } catch (error) {
      console.error('Controller validation failed:', error);
      validation.value = null;
    } finally {
      validating.value = false;
    }
  }

  async function handleAssign() {
    if (!canAssign.value) {return;}

    assigning.value = true;
    try {
      await api.post(
        `v2/site-equipment/${props.equipment.id}/assign-controller`,
        { controller_id: selectedController.value },
      );

      emit('assigned');
      emit('update:modelValue', false);

      // Reset state
      selectedController.value = null;
      validation.value = null;
      searchText.value = '';
    } catch (error) {
      console.error('Failed to assign controller:', error);
      Notify.create({ type: 'negative', message: 'Failed to assign controller. Please try again.' });
    } finally {
      assigning.value = false;
    }
  }

  function formatIoCapacity(capacity) {
    if (!capacity) {return 'N/A';}
    const parts = [];
    // Include all I/O types that have non-zero values
    const ioTypes = ['AI', 'AO', 'DI', 'DO', 'UI', 'UO'];
    ioTypes.forEach((type) => {
      if (capacity[type] && capacity[type] > 0) {
        parts.push(`${capacity[type]} ${type}`);
      }
    });
    return parts.length > 0 ? parts.join(', ') : 'No I/O';
  }

  // Helper functions
  function getAvailableIoTypes(capacity) {
    if (!capacity) {return [];}
    // Return only I/O types that have a non-zero capacity
    return Object.keys(capacity).filter((type) => capacity[type] > 0);
  }

  function getIoTypeColor(ioType) {
    const colors = {
      AI: 'blue',
      AO: 'green',
      DI: 'orange',
      DO: 'red',
      UI: 'purple',
      UO: 'indigo',
    };
    return colors[ioType] || 'grey';
  }

  // Watch for dialog open
  watch(() => props.modelValue, (isOpen) => {
    if (isOpen) {
      loadControllers();
    } else {
      // Reset state when dialog closes
      selectedController.value = null;
      validation.value = null;
      searchText.value = '';
    }
  });
</script>
